import axios from 'axios';
import { isDevAuthBypassEnabled } from '../app/devMode';
import { normalizeAppError } from './app-error';

// Track if we're already redirecting to prevent infinite loops
let isRedirecting = false;

interface CreateApiClientOptions {
  includeAuthHeader: boolean;
  redirectOnUnauthorized: boolean;
}

function createApiClient({ includeAuthHeader, redirectOnUnauthorized }: CreateApiClientOptions) {
  const client = axios.create({
    baseURL: import.meta.env.VITE_API_URL,
    timeout: 15_000 // 15 seconds
  });

  if (includeAuthHeader) {
    client.interceptors.request.use((config) => {
      const token = localStorage.getItem('LOCELLE:token');

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const appError = normalizeAppError(error);
      if (error?.response?.status) {
        localStorage.setItem('TEST_ERROR_STATUS', String(error.response.status));
        localStorage.setItem('TEST_ERROR_URL', String(error.config?.url));
      }

      // 1. Global Redirects (Infrastructure)
      if (appError.code === 'UNAUTHORIZED' && redirectOnUnauthorized && !isDevAuthBypassEnabled) {
        const lastRedirect = sessionStorage.getItem('BH:last_401_redirect');
        const now = Date.now();

        if (lastRedirect && now - parseInt(lastRedirect, 10) < 5000) {
          console.error('Detected 401 redirect loop. Stopping redirect.');
          return Promise.reject(appError);
        }

        if (!isRedirecting) {
          isRedirecting = true;
          sessionStorage.setItem('BH:last_401_redirect', now.toString());

          localStorage.removeItem('LOCELLE:token');
          console.warn('Session expired. Redirecting to home...');
          window.location.href = '/';
        }
      }

      // 2. Reject with Normalized Error
      // This allows feature-hooks (React Query) to access the structured AppError object.
      return Promise.reject(appError);
    }
  );

  return client;
}

export const api = createApiClient({
  includeAuthHeader: true,
  redirectOnUnauthorized: true
});

export const publicApi = createApiClient({
  includeAuthHeader: false,
  redirectOnUnauthorized: false
});
